import ExifReader from 'exifreader';
import get from 'lodash/get';

// Read DPI

const resolveRational = (value) => {
  if (!value) return 0;

  if (Array.isArray(value) && typeof value[0] === 'number') {
    const denominator = value[1] || 1;
    return value[0] / denominator;
  }

  if (Array.isArray(value) && typeof value[0] === 'object') {
    const { numerator, denominator } = value[0];
    if (denominator) return numerator / denominator;
  }

  // Case 3: ExifReader may pre-divide and return a float string like "415.0106"
  // Round to avoid floating-point drift from PPM round-trips
  return Math.round(Number(value)) || 0;
};

const readDpiFromBytes = (bytes) => {
  try {
    // expanded: true keeps JFIF and EXIF tag groups separate so resolution
    // tags from APP0 (JFIF) are not silently overwritten by APP1 (EXIF)
    const tags = ExifReader.load(bytes.buffer, { expanded: true });

    const jfifXRes = get(tags, 'jfif.XResolution.value');
    const jfifYRes = get(tags, 'jfif.YResolution.value');
    const jfifUnit = get(tags, 'jfif.ResolutionUnit.value');

    if (jfifXRes && jfifYRes) {
      const x = resolveRational(jfifXRes);
      const y = resolveRational(jfifYRes);
      if (x > 0 && y > 0) {
        // JFIF unit: 1 = DPI, 2 = DPCM
        if (jfifUnit === 2) {
          return { dpiX: Math.round(x * 2.54), dpiY: Math.round(y * 2.54) };
        }
        return { dpiX: Math.round(x), dpiY: Math.round(y) };
      }
    }

    // Fallback to EXIF (APP1)
    const exifXRes = get(tags, 'exif.XResolution.value');
    const exifYRes = get(tags, 'exif.YResolution.value');
    const exifUnit = get(tags, 'exif.ResolutionUnit.value');

    if (exifXRes && exifYRes) {
      const x = resolveRational(exifXRes);
      const y = resolveRational(exifYRes);
      if (x > 0 && y > 0) {
        // EXIF unit: 2 = inch (DPI), 3 = cm (DPCM)
        if (exifUnit === 3) {
          return { dpiX: Math.round(x * 2.54), dpiY: Math.round(y * 2.54) };
        }
        return { dpiX: Math.round(x), dpiY: Math.round(y) };
      }
    }

    // Fallback to flat tags (PNG / other formats)
    const flatXRes = get(tags, 'XResolution.value');
    const flatYRes = get(tags, 'YResolution.value');
    const flatUnit = get(tags, 'ResolutionUnit.value');

    if (flatXRes && flatYRes) {
      const x = resolveRational(flatXRes);
      const y = resolveRational(flatYRes);
      if (x > 0 && y > 0) {
        if (flatUnit === 3) {
          return { dpiX: Math.round(x * 2.54), dpiY: Math.round(y * 2.54) };
        }
        return { dpiX: Math.round(x), dpiY: Math.round(y) };
      }
    }

    return null;
  } catch (e) {
    return null;
  }
};

// Helpers

const readUint32 = (bytes, offset) => {
  if (offset + 4 > bytes.length) return 0;
  return (
    bytes[offset] * 0x1000000
    + bytes[offset + 1] * 0x10000
    + bytes[offset + 2] * 0x100
    + bytes[offset + 3]
  );
};

const writeUint32 = (target, offset, value) => {
  const arr = target;
  arr[offset] = Math.floor(value / 0x1000000) % 256;
  arr[offset + 1] = Math.floor(value / 0x10000) % 256;
  arr[offset + 2] = Math.floor(value / 0x100) % 256;
  arr[offset + 3] = value % 256;
};

const buildCrcTable = () => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let j = 0; j < 8; j += 1) {
      // eslint-disable-next-line no-bitwise
      c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
    }
    t[i] = c;
  }
  return t;
};

const crcTable = buildCrcTable();

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    crc = crcTable[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  // eslint-disable-next-line no-bitwise
  return (crc ^ 0xffffffff) >>> 0;
};

// JPEG

const isJpeg = (bytes) => bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xd8;

const writeJpegDpi = (bytes, dpi) => {
  const { dpiX } = dpi;
  const { dpiY } = dpi;
  let offset = 2;

  while (offset < bytes.length - 4) {
    if (bytes[offset] !== 0xff) break;

    const marker = bytes[offset + 1];
    const segLen = bytes[offset + 2] * 0x100 + bytes[offset + 3];

    if (segLen < 2 || offset + 2 + segLen > bytes.length) break;

    if (marker === 0xe0 && segLen >= 16) {
      const patched = new Uint8Array(bytes);
      patched[offset + 11] = 1;
      patched[offset + 12] = Math.floor(dpiX / 0x100) % 256;
      patched[offset + 13] = dpiX % 256;
      patched[offset + 14] = Math.floor(dpiY / 0x100) % 256;
      patched[offset + 15] = dpiY % 256;
      return patched;
    }

    offset += 2 + segLen;
  }

  const app0 = new Uint8Array([
    0xff, 0xe0,
    0x00, 0x10,
    0x4a, 0x46, 0x49, 0x46, 0x00,
    0x01, 0x01,
    0x01,
    Math.floor(dpiX / 0x100) % 256, dpiX % 256,
    Math.floor(dpiY / 0x100) % 256, dpiY % 256,
    0x00, 0x00,
  ]);

  const result = new Uint8Array(bytes.length + app0.length);
  result.set(bytes.slice(0, 2));
  result.set(app0, 2);
  result.set(bytes.slice(2), 2 + app0.length);
  return result;
};

// PNG

const PNG_SIGNATURE_LEN = 8;

const isPng = (bytes) => bytes.length >= 2 && bytes[0] === 0x89 && bytes[1] === 0x50;

const removePngChunk = (bytes, typeName) => {
  const parts = [bytes.slice(0, PNG_SIGNATURE_LEN)];
  let offset = PNG_SIGNATURE_LEN;

  while (offset + 12 <= bytes.length) {
    const len = readUint32(bytes, offset);
    if (offset + 12 + len > bytes.length) break;

    const type = String.fromCharCode(
      bytes[offset + 4],
      bytes[offset + 5],
      bytes[offset + 6],
      bytes[offset + 7],
    );

    if (type !== typeName) {
      parts.push(bytes.slice(offset, offset + 12 + len));
    }

    offset += 12 + len;
  }

  const total = parts.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(total);
  let pos = 0;
  parts.forEach((p) => {
    out.set(p, pos);
    pos += p.length;
  });

  return out;
};

const buildTextChunk = (keyword, value) => {
  const data = `${keyword}\x00${value}`;
  const dataBytes = new TextEncoder().encode(data);
  const chunk = new Uint8Array(12 + dataBytes.length);
  writeUint32(chunk, 0, dataBytes.length);
  chunk[4] = 0x74; chunk[5] = 0x45; chunk[6] = 0x58; chunk[7] = 0x74; // 'tEXt'
  chunk.set(dataBytes, 8);
  const crcVal = crc32(chunk.slice(4, 8 + dataBytes.length));
  writeUint32(chunk, 8 + dataBytes.length, crcVal);
  return chunk;
};

const buildXmpChunk = (dpiX, dpiY) => {
  const xmp = `<?xpacket begin="\uFEFF" id="W5M0MpCehiHzreSzNTczkc9d"?>
<x:xmpmeta xmlns:x="adobe:ns:meta/">
  <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
    <rdf:Description rdf:about=""
      xmlns:tiff="http://ns.adobe.com/tiff/1.0/"
      xmlns:exif="http://ns.adobe.com/exif/1.0/">
      <tiff:XResolution>${dpiX}/1</tiff:XResolution>
      <tiff:YResolution>${dpiY}/1</tiff:YResolution>
      <tiff:ResolutionUnit>2</tiff:ResolutionUnit>
      <exif:PixelXDimension>${dpiX}</exif:PixelXDimension>
      <exif:PixelYDimension>${dpiY}</exif:PixelYDimension>
    </rdf:Description>
  </rdf:RDF>
</x:xmpmeta>
<?xpacket end="w"?>`;

  return buildTextChunk('XML:com.adobe.xmp', xmp);
};

const writePngDpi = (bytes, dpi) => {
  const ppuX = Math.round(dpi.dpiX / 0.0254);
  const ppuY = Math.round(dpi.dpiY / 0.0254);

  const physChunk = new Uint8Array(21);
  writeUint32(physChunk, 0, 9);
  physChunk[4] = 0x70; physChunk[5] = 0x48; physChunk[6] = 0x59; physChunk[7] = 0x73;
  writeUint32(physChunk, 8, ppuX);
  writeUint32(physChunk, 12, ppuY);
  physChunk[16] = 1;
  writeUint32(physChunk, 17, crc32(physChunk.slice(4, 17)));

  const xmpChunk = buildXmpChunk(dpi.dpiX, dpi.dpiY);

  const cleaned = removePngChunk(removePngChunk(bytes, 'pHYs'), 'tEXt');

  const ihdrLen = readUint32(cleaned, PNG_SIGNATURE_LEN);
  const insertAt = PNG_SIGNATURE_LEN + 4 + 4 + ihdrLen + 4;

  if (insertAt > cleaned.length) return bytes;

  const extraLen = physChunk.length + xmpChunk.length;
  const result = new Uint8Array(cleaned.length + extraLen);
  result.set(cleaned.slice(0, insertAt));
  result.set(physChunk, insertAt);
  result.set(xmpChunk, insertAt + physChunk.length);
  result.set(cleaned.slice(insertAt), insertAt + extraLen);
  return result;
};

// API

// Reads DPI from an image URL.
export const readDpiFromUrl = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;

    const buffer = await response.arrayBuffer();
    const bytes = new Uint8Array(buffer);

    return readDpiFromBytes(bytes);
  } catch (e) {
    return null;
  }
};

// Writes DPI metadata into a blob.
export const writeDpiToBlob = async (blob, dpi, mimeType) => {
  if (!dpi) return blob;

  try {
    const buffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(buffer);

    if (isJpeg(bytes)) {
      return new Blob([writeJpegDpi(bytes, dpi)], { type: mimeType });
    }

    if (isPng(bytes)) {
      return new Blob([writePngDpi(bytes, dpi)], { type: mimeType });
    }

    return blob;
  } catch (e) {
    return blob;
  }
};
